/**
 * cart-tier-pricing.js
 *
 * Xử lý bậc thang giá (wholesale tiers) trong Cart / Cart Drawer.
 *
 * Bối cảnh: mỗi tier giá = 1 variant riêng với giá cố định/đơn vị.
 * API mặc định /cart/change.js chỉ đổi SỐ LƯỢNG của variant hiện có trên
 * line, không thể tự chuyển sang variant khác. Vì vậy khi khách đổi quantity
 * trong cart khiến số lượng rơi sang 1 tier khác, ta phải tự:
 *   1) Xoá line hiện tại (set quantity = 0)
 *   2) Thêm variant đúng tier mới với đúng số lượng khách nhập
 *   3) Yêu cầu cart-items / cart-drawer-items tự refresh lại UI
 *
 * AN TOÀN CHO CÁC PRODUCT KHÁC: script này chỉ chạy khi input quantity có
 * data-pricing-tiers (do snippet cart-tier-pricing-data.liquid xuất ra —
 * snippet đó chỉ xuất khi product có metafield custom.pricing_tiers).
 * Nếu không có, script return ngay và để cart.js gốc xử lý như cũ.
 */
(function () {
  if (window.__cartTierPricingLoaded) return; // tránh nạp trùng (cart page + drawer cùng lúc)
  window.__cartTierPricingLoaded = true;

  function safeParseJSON(str, fallback) {
    if (!str) return fallback;
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error('[cart-tier-pricing] JSON parse error:', e, str);
      return fallback;
    }
  }

  function findTierForQuantity(tiers, qty) {
    for (const key in tiers) {
      if (!Object.prototype.hasOwnProperty.call(tiers, key)) continue;
      const min = parseInt(tiers[key].min, 10) || 0;
      const max = tiers[key].max != null ? parseInt(tiers[key].max, 10) : Infinity;
      if (qty >= min && qty <= max) return key;
    }
    return null;
  }

  function findVariantIdForTier(variantTierMap, tierKey) {
    for (const variantId in variantTierMap) {
      if (!Object.prototype.hasOwnProperty.call(variantTierMap, variantId)) continue;
      if (variantTierMap[variantId] === tierKey) return variantId;
    }
    return null;
  }

  function getLineItemErrorElements(input) {
    const index = input.dataset.index;
    const errorBox =
      document.getElementById(`Line-item-error-${index}`) ||
      document.getElementById(`CartDrawer-LineItemError-${index}`);
    return errorBox ? errorBox.querySelector('.cart-item__error-text') : null;
  }

  function showLineError(input, message) {
    const errorText = getLineItemErrorElements(input);
    if (errorText) errorText.innerHTML = message;
  }

  function clearLineError(input) {
    const errorText = getLineItemErrorElements(input);
    if (errorText) errorText.innerHTML = '';
  }

  function setBusy(input, busy) {
    input.disabled = busy;
    const row = input.closest('.cart-item');
    if (row) row.classList.toggle('cart-item--tier-updating', busy);
  }

  function refreshCartUI() {
    // Tận dụng lại chính hàm onCartUpdate() đã có sẵn trong cart.js
    // (được định nghĩa trên custom element <cart-items> / <cart-drawer-items>),
    // để đảm bảo cách render lại HTML đồng nhất với luồng mặc định của theme.
    document.querySelectorAll('cart-items, cart-drawer-items').forEach((el) => {
      if (typeof el.onCartUpdate === 'function') el.onCartUpdate();
    });

    // Cập nhật số lượng trên icon giỏ hàng ở header (không được cart.js
    // tự refresh trong luồng pub/sub nội bộ, nên cập nhật thủ công ở đây).
    fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
      .then((res) => res.text())
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const newBubble = html.querySelector('cart-icon-bubble') || html.querySelector('#cart-icon-bubble');
        const oldBubble = document.querySelector('cart-icon-bubble') || document.querySelector('#cart-icon-bubble');
        if (newBubble && oldBubble) oldBubble.innerHTML = newBubble.innerHTML;
      })
      .catch(() => {
        /* không nghiêm trọng nếu bubble không refresh được */
      });
  }

  function swapLineToVariant(input, currentLine, targetVariantId, quantity) {
    setBusy(input, true);

    fetch(window.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: currentLine, quantity: 0 }),
    })
      .then((res) => res.json())
      .then((removeState) => {
        if (removeState.errors) {
          throw new Error(
            typeof removeState.errors === 'string' ? removeState.errors : JSON.stringify(removeState.errors)
          );
        }
        return fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(targetVariantId, 10), quantity: quantity }],
          }),
        });
      })
      .then((res) => res.json())
      .then((addState) => {
        if (addState.status || addState.errors) {
          throw new Error(addState.description || addState.message || 'Không thể cập nhật giỏ hàng theo bậc giá mới.');
        }
        clearLineError(input);
        refreshCartUI();
      })
      .catch((err) => {
        console.error('[cart-tier-pricing] swap error:', err);
        showLineError(
          input,
          'Có lỗi khi cập nhật số lượng theo bậc giá mới. Vui lòng thử lại hoặc tải lại trang.'
        );
        refreshCartUI(); // đồng bộ lại UI với trạng thái thật của cart (phòng trường hợp line đã bị xoá)
      })
      .finally(() => {
        setBusy(input, false);
      });
  }

  // Lưu lại giá trị hợp lệ gần nhất để có thể revert khi nhập sai
  document.addEventListener(
    'focus',
    (event) => {
      const input = event.target.closest('.quantity__input[data-pricing-tiers]');
      if (!input) return;
      input.dataset.previousValidValue = input.value;
    },
    true
  );

  // Bắt sự kiện change ở PHA CAPTURE, chạy trước handler của cart.js
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target.closest('.quantity__input[data-pricing-tiers]');
      if (!input) return; // Không phải sản phẩm có tier -> để cart.js xử lý bình thường

      const tiers = safeParseJSON(input.dataset.pricingTiers, null);
      const variantTierMap = safeParseJSON(input.dataset.variantTierMap, null);
      if (!tiers || !variantTierMap) return; // dữ liệu lỗi -> fallback về hành vi mặc định

      const newQty = parseInt(input.value, 10) || 0;
      const currentVariantId = input.dataset.quantityVariantId;
      const currentTierKey = variantTierMap[currentVariantId] || null;

      if (newQty <= 0) {
        // Số lượng 0 = xoá sản phẩm, để cart.js xử lý bình thường (không liên quan tier)
        return;
      }

      const targetTierKey = findTierForQuantity(tiers, newQty);

      if (!targetTierKey) {
        // Số lượng không rơi vào tier nào -> chặn lại, revert giá trị, báo lỗi
        event.preventDefault();
        event.stopImmediatePropagation();
        const revertTo = input.dataset.previousValidValue || input.getAttribute('value') || '1';
        input.value = revertTo;
        showLineError(
          input,
          `Số lượng ${newQty} không nằm trong bất kỳ mức giá nào. Vui lòng nhập số lượng hợp lệ.`
        );
        return;
      }

      if (targetTierKey === currentTierKey) {
        // Cùng tier -> chỉ đổi số lượng, để cart.js xử lý theo luồng mặc định (/cart/change.js)
        clearLineError(input);
        return;
      }

      // Khác tier -> chặn luồng mặc định của cart.js, tự xử lý swap variant
      event.preventDefault();
      event.stopImmediatePropagation();

      const targetVariantId = findVariantIdForTier(variantTierMap, targetTierKey);
      if (!targetVariantId) {
        input.value = input.dataset.previousValidValue || input.getAttribute('value') || '1';
        showLineError(input, 'Không tìm thấy mức giá phù hợp cho số lượng này. Vui lòng liên hệ hỗ trợ.');
        return;
      }

      const currentLine = parseInt(input.dataset.index, 10);
      input.dataset.previousValidValue = String(newQty);
      swapLineToVariant(input, currentLine, targetVariantId, newQty);
    },
    true // capture = true, chạy trước bubble-phase listener của cart.js
  );
})();
